import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import { DroppableCalendarDay } from './Droppable';
import DayInfo from './DayInfo/DayInfo';
import { useShootingPeriod } from './hooks/useShootingPeriod';
import { useCalDateShiftEdit, formatCalDateShiftDisplay } from './hooks/useCalDateShiftEdit';
import { useCalendarScroll } from './hooks/useCalendarScroll';
import { SlotInfo, CalDateInfo, SlotType, UpdateScheduleParams } from '../../../../types/schedule.types';
import { SceneInfo } from '../../../../types/scene.types';
import { ElementSceneInfo } from '../../../../types/element.types';
import DropdownMenu from '../../../../components/DropdownMenu/DropdownMenu';
import { parseDate } from '../../../../utils/dateUtils';
import './Calendar.css';

interface CalendarProps {
  canEdit?: boolean;
  scheduleID: string | undefined;
  filmStartShootingDate: string | null;
  scheduleStartDate?: string | null;
  scheduleEndDate?: string | null;
  selectedYear: number;
  selectedMonth: number;
  currentDate: Date | null;
  /** 当前拍摄计划的 currentDate 已完成初始化（缓存日期 / 开机日期），此前不要把视口滚走 */
  currentDateReady?: boolean;
  isScheduleTabActive: boolean;
  calendarDataLoading?: boolean;
  onSelectYearMonth: (year: number, month: number) => void;
  onDateSelect?: (date: Date) => void;
  slots?: SlotInfo[];
  calDates?: CalDateInfo[];
  scenes?: SceneInfo[];
  elementRelations?: ElementSceneInfo[];
  breakdownID?: string;  // 分场ID，用于从缓存获取数据
  // 拖拽状态
  disabledDateCells?: Set<string>;
  activeSortingId?: string | null;
  activeSortingType?: 'slot' | null;
  activeSortingDate?: string | null;
  activeSortingDragLevel?: 'slot' | 'note' | null;
  /** SlotType.SHOOT 地点头成组拖拽时，与 overlay 同步隐藏的源日行 id 列表 */
  activeSlotDragBundleIds?: string[] | null;
  dragOverDateCell?: string | null;
  invalidDropTargets?: Set<string>;
  optimisticCalDates?: CalDateInfo[];
  /** 场次/跨日单卡插入位高亮 */
  calendarInsertHighlight?: { dateStr: string; insertIndex: number } | null;
  /** 当前 dnd-kit 拖拽类型（用于插入区与 sortable 协调） */
  activeDragType?: 'slot' | 'scene' | 'wholeDay' | 'clipboard' | 'clipboardGroup' | null;
  /** 整日拖拽中的源日期 YYYY-MM-DD，供 DayInfo 隐藏源日卡片 */
  wholeDayDragSourceDateStr?: string | null;
  /** “显示所有地点”模式：UI 忽略 slotLocationVisibility */
  forceShowAllLocations?: boolean;
  /** 当某日 calDateSlotsSort 发生更新后，触发地点头归一化（按需在 showAllLocations=false 时执行） */
  normalizeDatesIfNeeded?: (dateStrs: string[]) => Promise<void>;
  updateCalDateSlotsSort?: (
    dateStr: string,
    additionalSoltIds?: string[],
    excludeSoltIds?: string[],
    replaceIds?: string[],
    calDateShift?: string,
  ) => Promise<void>;
  onSlotDelete?: (slotID: string) => void;
  onSlotCreate?: () => void;
  onCurrentDateInfoChange?: (info: {
    shootingDayNumber: number | null;
    totalDays: number | null;
    shift: string | undefined;
    totalPages: number;
    featuredRolesQuantity: number;
    crowdRolesQuantity: number;
    cast: string;
  }) => void;
  /** 服务端当前 scheduleTotalDays，与日历「当前/总日」分母比对后按需 PATCH */
  scheduleTotalDays?: number | null;
  /** 与日程页 useScheduleUpdates 同源，写回 scheduleTotalDays */
  patchSchedule?: (scheduleId: string, updates: UpdateScheduleParams) => Promise<void>;
}

/** 整日可拖拽的日期数字：仅当日有 SHOOT 或 NOTE 时启用 */
const DraggableDayNumber: React.FC<{
  dateStr: string;
  month: number;
  day: number;
  daySlots: SlotInfo[];
  calDateSlotsSort: string[] | undefined;
}> = ({ dateStr, month, day, daySlots, calDateSlotsSort }) => {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `wholeDay-${dateStr}`,
    data: {
      type: 'wholeDay',
      sourceDateStr: dateStr,
      slots: daySlots,
      calDateSlotsSort,
    },
  });
  return (
    <div
      ref={setNodeRef}
      className="calendar-day-number calendar-day-number-draggable"
      {...attributes}
      {...listeners}
    >
      <span className="calendar-day-month">{month}<span className="calendar-day-slash">-</span></span>
      <span className="calendar-day-day">{day}</span>
    </div>
  );
};

/** 不可拖拽的日期数字（当日无任务时） */
const PlainDayNumber: React.FC<{ month: number; day: number }> = ({ month, day }) => (
  <div className="calendar-day-number">
    <span className="calendar-day-month">{month}<span className="calendar-day-slash">-</span></span>
    <span className="calendar-day-day">{day}</span>
  </div>
);

/** 开机月-3 至 杀青月+3；若当前日期落在窗外，再向该侧多留 3 个月 */
const computeCalendarBounds = (
  scheduleStartDate: string | null | undefined,
  scheduleEndDate: string | null | undefined,
  currentDate: Date | null,
): { start: Date; end: Date } => {
  const today = new Date();
  const isNewSchedule = !scheduleStartDate || !scheduleEndDate;
  let start: Date;
  let end: Date;

  if (isNewSchedule) {
    start = new Date(today.getFullYear(), today.getMonth() - 3, 1);
    end = new Date(today.getFullYear(), today.getMonth() + 4, 0);
  } else {
    const startDate = parseDate(scheduleStartDate);
    const endDate = parseDate(scheduleEndDate);
    start = new Date(startDate.getFullYear(), startDate.getMonth() - 3, 1);
    end = new Date(endDate.getFullYear(), endDate.getMonth() + 4, 0);
  }

  if (currentDate && !isNaN(currentDate.getTime())) {
    const currentMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const currentMonthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    if (currentMonthStart < start) {
      start = new Date(currentMonthStart.getFullYear(), currentMonthStart.getMonth() - 3, 1);
    }
    if (currentMonthEnd > end) {
      end = new Date(currentMonthEnd.getFullYear(), currentMonthEnd.getMonth() + 4, 0);
    }
  }

  return { start, end };
};

const generateMonthRange = (start: Date, end: Date): Date[] => {
  const months: Date[] = [];
  const current = new Date(start);
  current.setDate(1);

  while (current <= end) {
    months.push(new Date(current));
    current.setMonth(current.getMonth() + 1);
  }

  return months;
};

const Calendar: React.FC<CalendarProps> = ({
  canEdit = false,
  scheduleID,
  scheduleStartDate,
  scheduleEndDate,
  selectedYear,
  selectedMonth,
  currentDate,
  currentDateReady = false,
  isScheduleTabActive,
  calendarDataLoading = false,
  onSelectYearMonth,
  onDateSelect,
  slots = [],
  calDates = [],
  scenes = [],
  elementRelations = [],
  breakdownID,
  disabledDateCells = new Set(),
  activeSortingId = null,
  activeSortingType = null,
  activeSortingDate = null,
  activeSortingDragLevel = null,
  activeSlotDragBundleIds = null,
  dragOverDateCell = null,
  invalidDropTargets = new Set(),
  optimisticCalDates: optimisticCalDatesProp = [],
  calendarInsertHighlight = null,
  activeDragType = null,
  wholeDayDragSourceDateStr = null,
  forceShowAllLocations = false,
  normalizeDatesIfNeeded,
  updateCalDateSlotsSort,
  onSlotDelete,
  onSlotCreate,
  onCurrentDateInfoChange,
  scheduleTotalDays,
  patchSchedule,
}) => {
  const { t } = useTranslation();
  const [dragOverCell, setDragOverCell] = useState<string | null>(null);
  // 原生 HTML5 drag and drop 的无效目标状态（用于从 ScheduleDetails 拖来的场次）
  const [invalidDropTargetsNative, setInvalidDropTargetsNative] = useState<Set<string>>(new Set());

  // 日历动态范围相关状态
  const [renderedMonths, setRenderedMonths] = useState<Date[]>([]);
  const [startMonth, setStartMonth] = useState<Date | null>(null);
  const [endMonth, setEndMonth] = useState<Date | null>(null);
  const pendingPrependScrollRef = useRef<number | null>(null);

  // 使用从 props 传入的拖拽状态和乐观更新状态
  const optimisticCalDates = optimisticCalDatesProp.length > 0 ? optimisticCalDatesProp : calDates;
  
  
  // 监听全局 dragend 事件来清理状态
  useEffect(() => {
    const handleGlobalDragEnd = () => {
      setInvalidDropTargetsNative(new Set());
      delete (window as any).__draggingSceneData__;
    };
    
    document.addEventListener('dragend', handleGlobalDragEnd);
    
    return () => {
      document.removeEventListener('dragend', handleGlobalDragEnd);
    };
  }, []);
  
  const { shootingPeriod, getShootingDayNumber } = useShootingPeriod(slots);

  // 与日历格 `calendar-day-shooting-period` 分母同源（useShootingPeriod.totalDays），写回服务器
  const calendarTotalDaysDenominator = useMemo(
    () => shootingPeriod?.totalDays ?? 0,
    [shootingPeriod?.totalDays],
  );
  const scheduleTotalDaysSyncTimerRef = useRef<number | null>(null);
  const isSyncingScheduleTotalDaysRef = useRef(false);
  useEffect(() => {
    if (!scheduleID || !patchSchedule) return;
    if (isSyncingScheduleTotalDaysRef.current) return;

    const next = calendarTotalDaysDenominator;
    const stored = scheduleTotalDays;
    if (stored === next) return;

    if (scheduleTotalDaysSyncTimerRef.current != null) {
      window.clearTimeout(scheduleTotalDaysSyncTimerRef.current);
      scheduleTotalDaysSyncTimerRef.current = null;
    }
    scheduleTotalDaysSyncTimerRef.current = window.setTimeout(() => {
      scheduleTotalDaysSyncTimerRef.current = null;
      isSyncingScheduleTotalDaysRef.current = true;

      patchSchedule(scheduleID, { scheduleTotalDays: next })
        .catch((error) => {
          console.error('自动同步拍摄计划 scheduleTotalDays 失败:', error);
        })
        .finally(() => {
          isSyncingScheduleTotalDaysRef.current = false;
        });
    }, 400);

    return () => {
      if (scheduleTotalDaysSyncTimerRef.current != null) {
        window.clearTimeout(scheduleTotalDaysSyncTimerRef.current);
        scheduleTotalDaysSyncTimerRef.current = null;
      }
    };
  }, [scheduleID, scheduleTotalDays, calendarTotalDaysDenominator, patchSchedule]);

  // 只在切换拍摄计划或开机/杀青边界变化时重建月份。
  // 点选已渲染的日期不能重建，否则会丢掉滚动时补出来的月份，视口被拉回再弹回去。
  const appliedBoundsKeyRef = useRef('');
  const appliedResolvedDateRef = useRef(false);
  const appliedRangeTimesRef = useRef<{ start: number; end: number } | null>(null);

  useEffect(() => {
    if (!scheduleID) return;

    const boundsKey = `${scheduleID}|${scheduleStartDate ?? ''}|${scheduleEndDate ?? ''}`;
    const boundsChanged = appliedBoundsKeyRef.current !== boundsKey;
    if (boundsChanged) {
      appliedBoundsKeyRef.current = boundsKey;
      appliedResolvedDateRef.current = false;
    }

    const resolveNow = currentDateReady && !appliedResolvedDateRef.current;
    if (!boundsChanged && !resolveNow) return;

    const { start, end } = computeCalendarBounds(scheduleStartDate, scheduleEndDate, currentDate);
    if (resolveNow) {
      appliedResolvedDateRef.current = true;
      const applied = appliedRangeTimesRef.current;
      const rangeUnchanged =
        !!applied && applied.start === start.getTime() && applied.end === end.getTime();
      if (rangeUnchanged && !boundsChanged) return;
    }

    appliedRangeTimesRef.current = { start: start.getTime(), end: end.getTime() };
    setStartMonth(start);
    setEndMonth(end);
    setRenderedMonths(generateMonthRange(start, end));
  }, [scheduleID, scheduleStartDate, scheduleEndDate, currentDateReady, currentDate]);

  // 向前加载月份（无限加载，不限制上界）
  const loadPreviousMonths = useCallback((count: number, scrollContainer?: HTMLDivElement | null) => {
    if (renderedMonths.length === 0) return;
    
    const firstRenderedMonth = renderedMonths[0];
    const newMonths: Date[] = [];
    
    for (let i = count; i >= 1; i--) {
      const newMonth = new Date(firstRenderedMonth);
      newMonth.setMonth(newMonth.getMonth() - i);
      newMonth.setDate(1);
      
      const exists = renderedMonths.some(m => 
        m.getFullYear() === newMonth.getFullYear() && 
        m.getMonth() === newMonth.getMonth()
      );
      if (!exists) newMonths.push(newMonth);
    }
    
    if (newMonths.length > 0 && scrollContainer) {
      pendingPrependScrollRef.current = scrollContainer.scrollHeight;
      setRenderedMonths(prev => [...newMonths, ...prev].sort((a, b) => a.getTime() - b.getTime()));
    } else if (newMonths.length > 0) {
      setRenderedMonths(prev => [...newMonths, ...prev].sort((a, b) => a.getTime() - b.getTime()));
    }
  }, [renderedMonths]);

  // 向后加载月份（无限加载，不限制下界）
  const loadNextMonths = useCallback((count: number) => {
    if (renderedMonths.length === 0) return;
    
    const lastRenderedMonth = renderedMonths[renderedMonths.length - 1];
    const newMonths: Date[] = [];
    
    for (let i = 1; i <= count; i++) {
      const newMonth = new Date(lastRenderedMonth);
      newMonth.setMonth(newMonth.getMonth() + i);
      newMonth.setDate(1);
      
      const exists = renderedMonths.some(m => 
        m.getFullYear() === newMonth.getFullYear() && 
        m.getMonth() === newMonth.getMonth()
      );
      if (!exists) newMonths.push(newMonth);
    }
    
    if (newMonths.length > 0) {
      setRenderedMonths(prev => [...prev, ...newMonths].sort((a, b) => a.getTime() - b.getTime()));
    }
  }, [renderedMonths]);

  // 先调用 useCalendarScroll 获取 calendarDaysContainerRef（需要在 useCalDateShiftEdit 之前）
  const { calendarContainerRef, calendarDaysContainerRef, currentDateRef } = useCalendarScroll({
    scheduleID,
    selectedYear,
    selectedMonth,
    onSelectYearMonth,
    isScheduleTabActive,
    currentDate,
    currentDateReady,
    renderedMonths,
    startMonth,
    endMonth,
    calendarDataLoading,
    loadPreviousMonths,
    loadNextMonths,
  });

  // 当前日期落到已渲染月份之外时只补月份，不重建整段（点选已有格子不会进这里）
  useEffect(() => {
    if (!currentDate || renderedMonths.length === 0) return;
    const year = currentDate.getFullYear();
    const monthIndex = currentDate.getMonth();
    const covered = renderedMonths.some(
      (d) => d.getFullYear() === year && d.getMonth() === monthIndex,
    );
    if (covered) return;

    const first = renderedMonths[0];
    const last = renderedMonths[renderedMonths.length - 1];
    const target = new Date(year, monthIndex, 1);

    if (target < first) {
      const months: Date[] = [];
      const cursor = new Date(year, monthIndex - 3, 1);
      while (cursor < first) {
        months.push(new Date(cursor));
        cursor.setMonth(cursor.getMonth() + 1);
      }
      if (months.length === 0) return;
      const container = calendarDaysContainerRef.current;
      if (container) pendingPrependScrollRef.current = container.scrollHeight;
      setRenderedMonths((prev) => {
        const prevFirst = prev[0];
        if (!prevFirst || prevFirst.getTime() <= months[0].getTime()) return prev;
        return [...months, ...prev];
      });
      return;
    }

    if (target > last) {
      const months: Date[] = [];
      const cursor = new Date(last.getFullYear(), last.getMonth() + 1, 1);
      const lastNeeded = new Date(year, monthIndex + 3, 1);
      while (cursor <= lastNeeded) {
        months.push(new Date(cursor));
        cursor.setMonth(cursor.getMonth() + 1);
      }
      if (months.length === 0) return;
      setRenderedMonths((prev) => {
        const prevLast = prev[prev.length - 1];
        const addedLast = months[months.length - 1];
        if (!prevLast || !addedLast || prevLast.getTime() >= addedLast.getTime()) return prev;
        return [...prev, ...months];
      });
    }
  }, [currentDate, renderedMonths, calendarDaysContainerRef]);

  // 向上加载后保持滚动位置（prepend 时需补偿新增高度）
  useLayoutEffect(() => {
    const prevHeight = pendingPrependScrollRef.current;
    if (prevHeight !== null && calendarDaysContainerRef.current) {
      const container = calendarDaysContainerRef.current;
      const newHeight = container.scrollHeight;
      container.scrollTop += newHeight - prevHeight;
      pendingPrependScrollRef.current = null;
    }
  }, [renderedMonths, calendarDaysContainerRef]);

  // 调用 useCalDateShiftEdit（在 useCalendarScroll 之后，可以使用 calendarDaysContainerRef）
  const {
    shiftDropdownVisible,
    setShiftDropdownVisible,
    shiftDropdownPosition,
    shiftDropdownWidth,
    shiftInputRefs,
    shiftEditingStates,
    handleShiftInputClick,
    handleShiftInputFocus,
    handleShiftInputChange,
    handleShiftInputBlur,
    handleShiftSelect,
    shiftMenuItems,
    formatDate,
    getCalDateByDate,
  } = useCalDateShiftEdit({
    scheduleID,
    calDates: calDates ?? [],
    onSlotCreate,
    calendarScrollContainerRef: calendarDaysContainerRef,
    onDateSelect,
  });

  // 获取日期所在周的起始日期（周日）
  const getWeekStart = (date: Date): Date => {
    const d = new Date(date);
    const dayOfWeek = d.getDay(); // 0 = 周日, 6 = 周六
    d.setDate(d.getDate() - dayOfWeek);
    return d;
  };

  // 处理拖拽离开
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // 只有当离开到单元格外部时才清除状态
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      const year = parseInt(e.currentTarget.dataset.year || '0');
      const month = parseInt(e.currentTarget.dataset.month || '0');
      const day = parseInt(e.currentTarget.dataset.day || '0');
      if (year && month && day) {
        const dateStr = formatDate(year, month, day);
        setInvalidDropTargetsNative(prev => {
          const newSet = new Set(prev);
          newSet.delete(dateStr);
          return newSet;
        });
      }
      setDragOverCell(null);
      e.currentTarget.style.cursor = '';
    }
  };
  
  // 处理拖拽结束（清理状态，用于从场次列表拖曳的原生 HTML5 drag and drop）
  const handleDragEnd = () => {
    setInvalidDropTargetsNative(new Set());
    setDragOverCell(null);
    delete (window as any).__draggingSceneData__;
  };

  // 处理日期单元格点击
  const handleDateCellClick = (year: number, month: number, day: number) => {
    const date = new Date(year, month - 1, day);
    onDateSelect?.(date);
  };




  // 生成日历星期行
  const renderWeekDays = () => {
    const weekdays = [t('schedule.weekdaySun'), t('schedule.weekdayMon'), t('schedule.weekdayTue'), t('schedule.weekdayWed'), t('schedule.weekdayThu'), t('schedule.weekdayFri'), t('schedule.weekdaySat')];
    return weekdays.map((day, index) => (
      <div key={index} className="calendar-weekday-cell">
        {day}
      </div>
    ));
  };

  // 生成多个月的日历日期（基于 renderedMonths）
  const renderCalendarDays = () => {
    const allElements: JSX.Element[] = [];
    
    if (renderedMonths.length === 0) {
      return allElements;
    }
    
    // 获取第一个月的第一天，计算需要填充的空白单元格数量
    const firstMonth = renderedMonths[0];
    const firstYear = firstMonth.getFullYear();
    const firstMonthNum = firstMonth.getMonth() + 1;
    const firstDayOfFirstMonth = new Date(firstYear, firstMonthNum - 1, 1).getDay(); // 0 = 周日, 6 = 周六
    
    // 在第一个月的第一天之前填充空白单元格，使其对齐到正确的星期
    for (let i = 0; i < firstDayOfFirstMonth; i++) {
      const cellIndex = i;
      const isLastInRow = (cellIndex + 1) % 7 === 0;
      allElements.push(
        <div 
          key={`empty-${i}`} 
          className={`calendar-day-cell empty ${isLastInRow ? 'last-in-row' : ''}`}
        >
        </div>
      );
    }
    
    // 计算所有月份的总天数，生成连续的日期序列
    let currentDateIndex = firstDayOfFirstMonth;
    
    // 遍历已渲染的月份
    renderedMonths.forEach((monthDate) => {
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth() + 1;
      const daysInMonth = new Date(year, month, 0).getDate();
      
      // 填充本月的所有日期
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month - 1, day);
        const weekStart = getWeekStart(date);
        const cellIndex = currentDateIndex;
        const isLastInRow = (cellIndex + 1) % 7 === 0;
        
        // 检查是否是当前日期
        const isCurrentDate = currentDate && 
          currentDate.getFullYear() === year &&
          currentDate.getMonth() + 1 === month &&
          currentDate.getDate() === day;
        
        // 检查该日期是否有 slot
        const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const hasSlot = slots && slots.length > 0 && slots.some(slot => {
          if (!slot.slotDate) return false;
          const slotDateStr = slot.slotDate.split('T')[0];
          return slotDateStr === dateStr;
        });
        // 当日 SHOOT 和 NOTE 类型的 slots（用于整日拖拽）
        const daySlots = (slots ?? []).filter(pt => {
          if (!pt.slotDate) return false;
          const ptDateStr = pt.slotDate.split('T')[0];
          if (ptDateStr !== dateStr) return false;
          return pt.slotType === SlotType.SHOOT || !pt.slotType || pt.slotType === SlotType.NOTE;
        });
        const calDate = getCalDateByDate(date);
        const hasDraggableSolts = daySlots.length > 0;
        
        const cellKey = `${year}-${month}-${day}`;
        // 检查是否是从场次列表拖曳（原生 HTML5 drag and drop）或跨日期拖曳（@dnd-kit）
        const isDragOver = dragOverCell === cellKey || dragOverDateCell === dateStr;
        const isInvalidDrop = invalidDropTargets.has(dateStr) || invalidDropTargetsNative.has(dateStr);
        
        allElements.push(
          <div
            key={cellKey}
            ref={isCurrentDate ? currentDateRef : null}
            className={`calendar-day-cell ${isLastInRow ? 'last-in-row' : ''} ${isCurrentDate ? 'active' : ''} ${!hasSlot ? 'empty-day' : ''} ${isDragOver && !isInvalidDrop ? 'drag-over' : ''} ${isInvalidDrop ? 'invalid-drop' : ''}`}
            data-year={year}
            data-month={month}
            data-day={day}
            data-week-start={weekStart.getTime()}
            onClick={() => handleDateCellClick(year, month, day)}
          >
            {isInvalidDrop ? (
              <div className="calendar-day-invalid-drop-overlay" aria-hidden="true">
                <span className="calendar-day-invalid-drop-icon">🚫</span>
                <div className="calendar-day-invalid-drop-hint-lines">
                  <span className="calendar-day-invalid-drop-hint-line">{t('schedule.duplicateSoltDropBlockedLine1')}</span>
                  <span className="calendar-day-invalid-drop-hint-line">{t('schedule.duplicateSoltDropBlockedLine2')}</span>
                </div>
              </div>
            ) : null}
            <div className="calendar-day-header">
            {hasDraggableSolts ? (
              <DraggableDayNumber
                dateStr={dateStr}
                month={month}
                day={day}
                daySlots={daySlots}
                calDateSlotsSort={calDate?.calDateSlotsSort}
              />
            ) : (
              <PlainDayNumber month={month} day={day} />
            )}
            {(() => {
              const calDate = getCalDateByDate(date);
              const dateKey = formatDate(year, month, day);
              const isShiftDropdownOpen = shiftDropdownVisible === dateKey;
              
              const editingState = shiftEditingStates[dateKey];
              const isEditing = editingState?.isEditing || false;
              const displayCalDateShift = formatCalDateShiftDisplay(calDate?.calDateShift, t);
              const rawShift = calDate?.calDateShift;
              const shiftDropdownSelectedId =
                rawShift === 'MORNING' || rawShift === 'AFTERNOON' || rawShift === 'EVENING'
                  ? rawShift
                  : shiftMenuItems.find((i) => i.label === rawShift)?.id ?? null;
              const inputValue = isEditing ? (editingState?.value ?? '') : (displayCalDateShift || editingState?.value || '');
              
              return (
                <>
                  <input
                    ref={(el) => { shiftInputRefs.current[dateKey] = el; }}
                    type="text"
                    className={`calendar-day-shift ${isEditing ? 'calendar-day-shift-editing' : ''}`}
                    value={inputValue}
                    placeholder={!inputValue ? t('schedule.shiftPlaceholder') : ''}
                    onClick={(e) => handleShiftInputClick(e, date)}
                    onFocus={(e) => handleShiftInputFocus(e, date)}
                    onChange={(e) => handleShiftInputChange(e, date)}
                    onBlur={(e) => handleShiftInputBlur(e, date)}
                  />
                  {isShiftDropdownOpen && shiftDropdownPosition && (
                    <DropdownMenu
                      items={shiftMenuItems}
                      visible={true}
                      onClose={() => setShiftDropdownVisible(null)}
                      onSelect={(item) => handleShiftSelect(date, item.label)}
                      position={shiftDropdownPosition}
                      useFixedPosition={true}
                      width={shiftDropdownWidth ?? undefined}
                      className="dropdown-menu-calendar-shift"
                      selectedValue={shiftDropdownSelectedId}
                      dataDropdownId={`shift-dropdown-${dateKey}`}
                    />
                  )}
                </>
              );
            })()}
            {(() => {
              const dayNumber = getShootingDayNumber(date);
              const shouldShowContent = dayNumber !== null && shootingPeriod;
              return (
                <div 
                  className="calendar-day-shooting-period" 
                  style={{ visibility: shouldShowContent ? 'visible' : 'hidden' }}
                >
                  {shouldShowContent
                    ? t('schedule.calendarShootingPeriodCell', {
                        current: dayNumber,
                        total: shootingPeriod.totalDays,
                      })
                    : '\u00A0'}
                </div>
              );
            })()}
            </div>
            <DroppableCalendarDay
              id={`calendar-day-${dateStr}`}
              className="calendar-day-content"
            >
              <DayInfo
                canEdit={canEdit}
                date={date}
                slots={slots}
                calDates={optimisticCalDates}
                scenes={scenes}
                elementRelations={elementRelations}
                breakdownID={breakdownID}
                scheduleID={scheduleID}
                forceShowAllLocations={forceShowAllLocations}
                normalizeDatesIfNeeded={normalizeDatesIfNeeded}
                updateCalDateSlotsSort={updateCalDateSlotsSort}
                onSlotDelete={onSlotDelete}
                onSlotOrderChange={onSlotCreate}
                disabledDateCells={disabledDateCells}
                activeSortingId={activeSortingId}
                activeSortingType={activeSortingType}
                activeSortingDate={activeSortingDate}
                activeSortingDragLevel={activeSortingDragLevel}
                activeSlotDragBundleIds={activeSlotDragBundleIds}
                currentDate={currentDate}
                shootingPeriod={shootingPeriod}
                getShootingDayNumber={getShootingDayNumber}
                onCurrentDateInfoChange={onCurrentDateInfoChange}
                onSelectCalendarDay={() => onDateSelect?.(date)}
                wholeDayDragSourceDateStr={wholeDayDragSourceDateStr}
                calendarInsertHighlight={calendarInsertHighlight}
                enableCalendarInsertHitZones={
                  activeDragType === 'scene' ||
                  (activeDragType === 'slot' &&
                    activeSortingDate != null &&
                    activeSortingDate !== dateStr)
                }
                disableInDaySortableForExternalInsert={
                  dragOverDateCell === dateStr &&
                  (activeDragType === 'scene' ||
                    (activeDragType === 'slot' &&
                      activeSortingDate != null &&
                      activeSortingDate !== dateStr))
                }
              />
            </DroppableCalendarDay>
          </div>
        );
        
        currentDateIndex++;
      }
    });
    
    return allElements;
  };

  return (
    <div className="schedule-calendar-view" ref={calendarContainerRef}>
      {/* 星期行与日期网格同一滚动容器，避免 Windows 经典滚动条占宽导致与表头列错位；表头 sticky */}
      <div
        className="calendar-days-container"
        ref={calendarDaysContainerRef}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragOverCell(null);
            setInvalidDropTargetsNative(new Set());
          }
        }}
        onDragEnd={handleDragEnd}
      >
        <div className="calendar-weekday-row calendar-weekday-row--sticky">{renderWeekDays()}</div>
        <div className="calendar-days-grid">{renderCalendarDays()}</div>
      </div>
    </div>
  );
};

export default Calendar;
